import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 4,
  },
  colors: {
    primary: {
      main: "#102B4E",
      dark: "#1D394F",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFFFFF",
      dark: "#1D394F",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    text: {
      primary: "#141414",
      secondary: "#000000",
      hint: "#808890",
    },
    info: {
      main: "#1E369B",
      light: "#DBDFEF",
    },
    background: "#FFFFFF",
    backgroundHeader: "#FFFFFF",
    backgroundPrice: "#102B4E",
    border: "#ADADAD",
    hover: "#DEE1E3",
    placeholder: "#666666",
    transparent: "#00000000",
    divider: "#EEE",
    selectedBackground: "#F5F5F5",
    descriptionCardBackground: "#D6D6D6",
    infoBackground: "#DBDFEF",
  },
};
const svgIcons = {
  circleRemove: {
    d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const ford = {
  themeName: "Ford",
  brand: BrandsEnum.Ford,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.selectedBackground,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.selectedBackground,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("Ford"),
    color: variables.colors.text.primary,
    h1: {
      fontFamily: getFontFamily("Ford"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontFamily: getFontFamily("Ford"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontFamily: getFontFamily("Ford"),
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 400,
      textDecoration: "underline",
    },
    h4: {
      fontFamily: getFontFamily("Ford"),
      color: variables.colors.text.primary,
      fontWeight: 400,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily("Ford"),
      fontSize: "1.25rem",
      color: variables.colors.text.primary,
      fontWeight: 700,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
      borderRadius: 4,
    },
    cardSelected: {
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      backgroundColor: variables.colors.background,
    },
    header: {
      textColor: variables.colors.text.primary,
      fontWeight: 400,
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 24,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
      },
      containedPrimary: {
        backgroundColor: variables.colors.primary.main,
        fontWeight: 400,
        color: variables.colors.primary.contrastText,
        textDecoration: "none",
        "&:hover": {
          border: "1px solid " + variables.colors.primary.dark,
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.primary.dark,
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        "&:hover": {
          border: "1px solid " + variables.colors.secondary.dark,
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.primary.main,
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          border: "1px solid " + variables.colors.secondary.dark,
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.primary.main,
          "& span": {
            textDecoration: "none",
          },
        },
        color: variables.colors.secondary.contrastText,
        backgroundColor: variables.colors.secondary.main,
        border: "1px solid " + variables.colors.secondary.main,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          //color:"black",
          backgroundColor: variables.colors.background,
          border: "1px solid " + variables.colors.text.secondary,
          "&:hover": {
            border: "1px solid " + "#375F9F",
            color: "#FFFFFF",
            backgroundColor: "#375F9F",
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            border: "1px solid " + "#375F9F",
            color: "#FFFFFF",
            backgroundColor: "#375F9F",
            "& span": {
              textDecoration: "none",
            },
          },
        },
      },
      outlinedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.main,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
        },
        "&:active": {
          border: "2px solid " + variables.colors.text.hint,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
        },
        color: variables.colors.primary.main,
        border: "2px solid " + variables.colors.primary.main,
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        "&:hover": {
          color: variables.colors.secondary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          backgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.background,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        fontFamily: getFontFamily("Ford"),
        color: variables.colors.text.primary,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        fontFamily: getFontFamily("Ford"),
        color: variables.colors.text.primary,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: "1.75rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        border: "1px solid " + variables.colors.border,
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.background,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: "#333333",
          textDecoration: "underline",
          "&:hover": {
            color: "#333333",
            fontWeight: 500,
          },
          "&:active": {
            color: "#333333",
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.placeholder,
        border: "1px solid " + variables.colors.border,
        fontWeight: 400,
        "&$selected": {
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          borderColor: variables.colors.primary.main,
          fontWeight: 500,
          "&:hover": {
            backgroundColor: variables.colors.transparent,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.transparent,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: variables.colors.backgroundPrice,
    textColor: variables.colors.secondary.contrastText,
  },
  priceText: {
    totalPriceColor: variables.colors.text.primary,
    fontWeight: 400,
  },
  header: {
    "& *": {
      color: variables.colors.primary.main,
    },
  },
  outlinedButton: {
    "& span": {
      color: variables.colors.text.secondary,
      width: "100%",
      height: "100%",
      minHeight: "56px",
    },
    "& span:hover": {
      color: variables.colors.primary.contrastText,
    },
    padding: "0",
    display: "flex",
  },
  popup: {
    root: {
      fontFamily: getFontFamily("Ford"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#102B4E",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Ford"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  statusIndicator: {
    borderRadius: 4,
  },
  icons: {
    dateTimeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4.69492 22C4.20527 22 3.80038 21.8399 3.48023 21.5198C3.16008 21.1996 3 20.7947 3 20.3051V6.29379C3 5.80414 3.16008 5.39925 3.48023 5.0791C3.80038 4.75895 4.20527 4.59887 4.69492 4.59887H6.9548V2H7.85876V4.59887H15.6554V2H16.4463V4.59887H18.7062C19.1959 4.59887 19.6008 4.75895 19.9209 5.0791C20.2411 5.39925 20.4011 5.80414 20.4011 6.29379V20.3051C20.4011 20.7947 20.2411 21.1996 19.9209 21.5198C19.6008 21.8399 19.1959 22 18.7062 22H4.69492ZM4.69492 21.209H18.7062C18.9322 21.209 19.1394 21.1149 19.3277 20.9266C19.516 20.7382 19.6102 20.5311 19.6102 20.3051V10.8136H3.79096V20.3051C3.79096 20.5311 3.88512 20.7382 4.07345 20.9266C4.26177 21.1149 4.46893 21.209 4.69492 21.209ZM3.79096 10.0226H19.6102V6.29379C19.6102 6.0678 19.516 5.86064 19.3277 5.67232C19.1394 5.48399 18.9322 5.38983 18.7062 5.38983H4.69492C4.46893 5.38983 4.26177 5.48399 4.07345 5.67232C3.88512 5.86064 3.79096 6.0678 3.79096 6.29379V10.0226ZM11.7006 14.6554C11.4934 14.6554 11.3098 14.5753 11.1497 14.4153C10.9896 14.2552 10.9096 14.0716 10.9096 13.8644C10.9096 13.6573 10.9896 13.4736 11.1497 13.3136C11.3098 13.1535 11.4934 13.0734 11.7006 13.0734C11.9077 13.0734 12.0913 13.1535 12.2514 13.3136C12.4115 13.4736 12.4915 13.6573 12.4915 13.8644C12.4915 14.0716 12.4115 14.2552 12.2514 14.4153C12.0913 14.5753 11.9077 14.6554 11.7006 14.6554ZM7.18079 14.6554C6.97363 14.6554 6.79002 14.5753 6.62994 14.4153C6.46987 14.2552 6.38983 14.0716 6.38983 13.8644C6.38983 13.6573 6.46987 13.4736 6.62994 13.3136C6.79002 13.1535 6.97363 13.0734 7.18079 13.0734C7.38795 13.0734 7.57156 13.1535 7.73164 13.3136C7.89171 13.4736 7.97175 13.6573 7.97175 13.8644C7.97175 14.0716 7.89171 14.2552 7.73164 14.4153C7.57156 14.5753 7.38795 14.6554 7.18079 14.6554ZM16.2203 14.6554C16.0132 14.6554 15.8296 14.5753 15.6695 14.4153C15.5094 14.2552 15.4294 14.0716 15.4294 13.8644C15.4294 13.6573 15.5094 13.4736 15.6695 13.3136C15.8296 13.1535 16.0132 13.0734 16.2203 13.0734C16.4275 13.0734 16.6111 13.1535 16.7712 13.3136C16.9313 13.4736 17.0113 13.6573 17.0113 13.8644C17.0113 14.0716 16.9313 14.2552 16.7712 14.4153C16.6111 14.5753 16.4275 14.6554 16.2203 14.6554ZM11.7006 18.9492C11.4934 18.9492 11.3098 18.8691 11.1497 18.709C10.9896 18.549 10.9096 18.3653 10.9096 18.1582C10.9096 17.951 10.9896 17.7674 11.1497 17.6073C11.3098 17.4473 11.4934 17.3672 11.7006 17.3672C11.9077 17.3672 12.0913 17.4473 12.2514 17.6073C12.4115 17.7674 12.4915 17.951 12.4915 18.1582C12.4915 18.3653 12.4115 18.549 12.2514 18.709C12.0913 18.8691 11.9077 18.9492 11.7006 18.9492ZM7.18079 18.9492C6.97363 18.9492 6.79002 18.8691 6.62994 18.709C6.46987 18.549 6.38983 18.3653 6.38983 18.1582C6.38983 17.951 6.46987 17.7674 6.62994 17.6073C6.79002 17.4473 6.97363 17.3672 7.18079 17.3672C7.38795 17.3672 7.57156 17.4473 7.73164 17.6073C7.89171 17.7674 7.97175 17.951 7.97175 18.1582C7.97175 18.3653 7.89171 18.549 7.73164 18.709C7.57156 18.8691 7.38795 18.9492 7.18079 18.9492ZM16.2203 18.9492C16.0132 18.9492 15.8296 18.8691 15.6695 18.709C15.5094 18.549 15.4294 18.3653 15.4294 18.1582C15.4294 17.951 15.5094 17.7674 15.6695 17.6073C15.8296 17.4473 16.0132 17.3672 16.2203 17.3672C16.4275 17.3672 16.6111 17.4473 16.7712 17.6073C16.9313 17.7674 17.0113 17.951 17.0113 18.1582C17.0113 18.3653 16.9313 18.549 16.7712 18.709C16.6111 18.8691 16.4275 18.9492 16.2203 18.9492Z'
          fill='#102B4E'
        />
      </svg>
    ),
    selectIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M4 8L12 16.0018L20 8' stroke='#102B4E' stroke-miterlimit='10' />
      </svg>
    ),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M4.65574 20L4 19.3443L11.3443 12L4 4.65574L4.65574 4L12 11.3443L19.3443 4L20 4.65574L12.6557 12L20 19.3443L19.3443 20L12 12.6557L4.65574 20Z'
            fill='#102B4E'
          />
        </g>
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.487 17H12.5304V10.8869H11.487V17ZM11.997 9.33042C12.1918 9.33042 12.3511 9.26946 12.475 9.14754C12.5989 9.02561 12.6609 8.86727 12.6609 8.67253C12.6609 8.4778 12.5999 8.31847 12.478 8.19456C12.356 8.07064 12.1977 8.00869 12.003 8.00869C11.8082 8.00869 11.6489 8.06966 11.525 8.19159C11.4011 8.31351 11.3391 8.47184 11.3391 8.66657C11.3391 8.86131 11.4001 9.02063 11.522 9.14455C11.644 9.26846 11.8023 9.33042 11.997 9.33042ZM12.0052 22C10.6223 22 9.3217 21.7373 8.10328 21.2119C6.88486 20.6865 5.825 19.9735 4.9237 19.0729C4.02241 18.1722 3.3098 17.1144 2.78588 15.8995C2.26196 14.6846 2 13.3865 2 12.0052C2 10.6223 2.26269 9.3217 2.78808 8.10328C3.31347 6.88486 4.02649 5.825 4.92715 4.9237C5.82782 4.0224 6.88561 3.3098 8.10051 2.78588C9.3154 2.26196 10.6135 2 11.9948 2C13.3777 2 14.6783 2.26269 15.8967 2.78808C17.1151 3.31347 18.175 4.02649 19.0763 4.92715C19.9776 5.82782 20.6902 6.88561 21.2141 8.10051C21.738 9.3154 22 10.6135 22 11.9948C22 13.3777 21.7373 14.6783 21.2119 15.8967C20.6865 17.1151 19.9735 18.175 19.0729 19.0763C18.1722 19.9776 17.1144 20.6902 15.8995 21.2141C14.6846 21.738 13.3865 22 12.0052 22ZM11.9993 20.9565C14.4918 20.9565 16.6078 20.087 18.3473 18.348C20.0868 16.609 20.9565 14.4932 20.9565 12.0007C20.9565 9.50821 20.087 7.39221 18.348 5.65272C16.609 3.91322 14.4932 3.04348 12.0007 3.04348C9.50821 3.04348 7.39221 3.91298 5.65272 5.65198C3.91322 7.39101 3.04348 9.50678 3.04348 11.9993C3.04348 14.4918 3.91298 16.6078 5.65198 18.3473C7.39101 20.0868 9.50678 20.9565 11.9993 20.9565Z'
          fill='#102B4E'
        />
      </svg>
    ),
    infoIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.487 17H12.5304V10.8869H11.487V17ZM11.997 9.33042C12.1918 9.33042 12.3511 9.26946 12.475 9.14754C12.5989 9.02561 12.6609 8.86727 12.6609 8.67253C12.6609 8.4778 12.5999 8.31847 12.478 8.19456C12.356 8.07064 12.1977 8.00869 12.003 8.00869C11.8082 8.00869 11.6489 8.06966 11.525 8.19159C11.4011 8.31351 11.3391 8.47184 11.3391 8.66657C11.3391 8.86131 11.4001 9.02063 11.522 9.14455C11.644 9.26846 11.8023 9.33042 11.997 9.33042ZM12.0052 22C10.6223 22 9.3217 21.7373 8.10328 21.2119C6.88486 20.6865 5.825 19.9735 4.9237 19.0729C4.02241 18.1722 3.3098 17.1144 2.78588 15.8995C2.26196 14.6846 2 13.3865 2 12.0052C2 10.6223 2.26269 9.3217 2.78808 8.10328C3.31347 6.88486 4.02649 5.825 4.92715 4.9237C5.82782 4.0224 6.88561 3.3098 8.10051 2.78588C9.3154 2.26196 10.6135 2 11.9948 2C13.3777 2 14.6783 2.26269 15.8967 2.78808C17.1151 3.31347 18.175 4.02649 19.0763 4.92715C19.9776 5.82782 20.6902 6.88561 21.2141 8.10051C21.738 9.3154 22 10.6135 22 11.9948C22 13.3777 21.7373 14.6783 21.2119 15.8967C20.6865 17.1151 19.9735 18.175 19.0729 19.0763C18.1722 19.9776 17.1144 20.6902 15.8995 21.2141C14.6846 21.738 13.3865 22 12.0052 22ZM11.9993 20.9565C14.4918 20.9565 16.6078 20.087 18.3473 18.348C20.0868 16.609 20.9565 14.4932 20.9565 12.0007C20.9565 9.50821 20.087 7.39221 18.348 5.65272C16.609 3.91322 14.4932 3.04348 12.0007 3.04348C9.50821 3.04348 7.39221 3.91298 5.65272 5.65198C3.91322 7.39101 3.04348 9.50678 3.04348 11.9993C3.04348 14.4918 3.91298 16.6078 5.65198 18.3473C7.39101 20.0868 9.50678 20.9565 11.9993 20.9565Z'
          fill='#102B4E'
        />
      </svg>
    ),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M6.35547 10.1949L11.2901 15.1279L21.9999 4.41974' stroke='#102B4E' stroke-miterlimit='10' />
        <path
          d='M19.1028 4.96822C18.8882 4.75206 18.664 4.54396 18.4317 4.34715C16.6927 2.8824 14.4472 2 11.9952 2C6.47493 2 2 6.47491 2 11.9935C2 17.5122 6.47493 21.9871 11.9952 21.9871C17.5154 21.9871 21.9903 17.5122 21.9903 11.9935C21.9903 10.9627 21.8339 9.96741 21.5435 9.03178C21.3967 8.56074 21.2176 8.1026 21.0063 7.66382'
          stroke='#102B4E'
          stroke-miterlimit='10'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(
      `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.35547 10.1949L11.2901 15.1279L21.9999 4.41974" stroke="#102B4E" stroke-miterlimit="10"/>
      <path d="M19.1028 4.96822C18.8882 4.75206 18.664 4.54396 18.4317 4.34715C16.6927 2.8824 14.4472 2 11.9952 2C6.47493 2 2 6.47491 2 11.9935C2 17.5122 6.47493 21.9871 11.9952 21.9871C17.5154 21.9871 21.9903 17.5122 21.9903 11.9935C21.9903 10.9627 21.8339 9.96741 21.5435 9.03178C21.3967 8.56074 21.2176 8.1026 21.0063 7.66382" stroke="#102B4E" stroke-miterlimit="10"/>
      </svg>`,
    ),
    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#102B4E' stroke='#102B4E' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <path
          transform='translate(12 3) scale(0.4)'
          d='M19.1028 4.96822C18.8882 4.75206 18.664 4.54396 18.4317 4.34715C16.6927 2.8824 14.4472 2 11.9952 2C6.47493 2 2 6.47491 2 11.9935C2 17.5122 6.47493 21.9871 11.9952 21.9871C17.5154 21.9871 21.9903 17.5122 21.9903 11.9935C21.9903 10.9627 21.8339 9.96741 21.5435 9.03178C21.3967 8.56074 21.2176 8.1026 21.0063 7.66382'
          stroke='#FFFFFF'
          stroke-miterlimit='10'
        />
        <path
          transform='translate(12 3) scale(0.4)'
          d='M6.35547 10.1949L11.2901 15.1279L21.9999 4.41974'
          stroke='#FFFFFF'
          fill='transparent'
          stroke-miterlimit='10'
        />
      </>
    ),
    backIcon: (
      <path
        {...defaultPathParams}
        stroke={variables.colors.primary.main}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 15,6 -6,6 6,6'
      />
    ),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: variables.colors.infoBackground,
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
    borderRadius: 4,
  },
  coverageSelect: {
    root: {
      CoverageHeaderName: {
        color: variables.colors.text.secondary,
      },
      CoverageItemText: {
        color: variables.colors.text.primary,
      },
      CoverageItemList: {
        padding: "10px 0 0 0",
      },
    },
    selected: {
      backgroundColor: variables.colors.selectedBackground,
      CoverageHeaderName: {
        fontWeight: 700,
      },
      CoverageHeaderPrice: {
        fontWeight: 700,
      },
      textColor: variables.colors.primary.main,
      borderWidth: 2,
      paddingBottom: 0,
    },
    descriptionCard: {
      backgroundColor: variables.colors.descriptionCardBackground,
      borderColor: variables.colors.selectedBackground,
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.backgroundPrice,
    },
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  paymentOption: {
    selectedPayment: {
      borderWidth: 2,
    },
    label: {
      lineHeight: "1.75rem",
    },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
  },
};

export default createTheme(ford);
